import { ServerError } from '@apollo/client';
import type { InferGetServerSidePropsType, NextPage } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { Shortnews } from '#components/Shortnews/Shortnews';
import { buildItemUrlAbsolute } from '#components/Shortnews/Shortnews.utils';
import { ShortnewsOverviewDocument } from '#components/Shortnews/__generated__/Shortnews.generated';
import { NAVIGATION_ITEM_SHORTNEWS } from '#config/constants';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { buildContentPageSeoConfig } from '#cutils/seo/buildContentPageSeoConfig';
import { throwOnGraphQLSemanticErrorForPaths } from '#sutils/graphql-error-matcher';
import { logger } from '#utils/logging/logger';
import { formatTimestampShortnews } from '#utils/time/formatTimestampShortnews';
import { PageView } from 'react-tracktor';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const SEO_DESCRIPTION =
	'Bleiben Sie mit den BR24-Meldungen auf dem Laufenden: Aktuelle Nachrichten zu Bayern, Deutschland und der Welt in aller Kürze im Überblick.';
const SEO_TITLE = 'Aktuelle Meldungen von BR24 im Überblick';

const Page: NextPage<Props> = ({ shortnewsList, formattedDate, timestamp, newsAudio }) => {
	const { constants } = useEnvironment();

	const seoConfig = buildContentPageSeoConfig({
		description: SEO_DESCRIPTION,
		title: SEO_TITLE,
		url: buildItemUrlAbsolute({ omId: null, slug: null }, constants.appUrl),
	});

	const trackingData = {
		...NAVIGATION_ITEM_SHORTNEWS,
		pageType: NAVIGATION_ITEM_SHORTNEWS.pageType,
	};

	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>
			<PageView key="shortnews" pageViewData={{ data: trackingData, event: { type: 'PageViewEvent' } }} />

			<Shortnews timestamp={timestamp} shortNewsList={shortnewsList} formattedDate={formattedDate} newsAudio={newsAudio} />
		</>
	);
};

const OPENMEDIA_PATH = ['openmedia'];

export const getServerSideProps = createDefaultGetServerSideProps(async (_ctx, apolloClient, withCacheTags) => {
	const navigationTitle = NAVIGATION_ITEM_SHORTNEWS.pageTitle;

	try {
		const { data, error } = await apolloClient.query({
			query: ShortnewsOverviewDocument,
		});

		throwOnGraphQLSemanticErrorForPaths([OPENMEDIA_PATH], error);
		if (ServerError.is(error)) {
			throw error;
		}

		const newsItems = data?.openmedia?.mainList?.edges?.[0]?.node;

		if (!newsItems) {
			throw new Error(`No data returned for openmedia field`);
		}

		const shortnewsList = (newsItems.items?.edges?.map((edge) => edge?.node) ?? [])
			.filter((article) => notNullOrUndefined(article))
			.filter((article) => !!article.title);

		const timestamp = newsItems.lastUpdated || null;
		const formattedDate = timestamp ? formatTimestampShortnews(timestamp) : null;

		if (shortnewsList.length === 0) {
			logger.warn('Shortnews list is empty');
			return { notFound: true };
		}

		const cacheTags = new Set<CacheTag>();
		if (withCacheTags) {
			for (const { omId } of shortnewsList) {
				cacheTags.add(`sn.${omId}`);
			}
		}

		return {
			props: { shortnewsList, formattedDate, timestamp, navigationTitle, cacheTags, newsAudio: data?.newsAudio },
		};
	} catch (error: any) {
		logger.error(error, `Error during meldungen resolution`);
		throw error;
	}
});

export default Page;
